<template>
  <a-modal
    :open="visible"
    :title="title"
    width="520px"
    :footer="null"
    :mask-closable="!running"
    :closable="!running"
    @cancel="emit('close')"
  >
    <ul class="hc-steps">
      <li v-for="(s, i) in rows" :key="s.key" class="hc-step" :class="`is-${s.status}`">
        <span class="hc-icon">
          <LoadingOutlined v-if="s.status === 'running'" />
          <CheckCircleFilled v-else-if="s.status === 'ok'" />
          <ExclamationCircleFilled v-else-if="s.status === 'warn'" />
          <CloseCircleFilled v-else-if="s.status === 'fail'" />
          <span v-else class="hc-dot" />
        </span>
        <span v-if="i < rows.length - 1" class="hc-line" />
        <div class="hc-body">
          <div class="hc-title">
            {{ s.title }}
            <span v-if="s.ms !== undefined" class="hc-ms">{{ s.ms }} ms</span>
          </div>
          <div v-if="s.detail" class="hc-detail">{{ s.detail }}</div>
        </div>
      </li>
    </ul>

    <div v-if="!running && result" class="hc-summary" :class="`is-${summary.level}`">
      {{ summary.text }}
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons-vue';
import type { HealthcheckResult } from '../api/proxies';
import { buildHealthcheckSteps, type HealthcheckStepView } from './healthcheckSteps';

const props = defineProps<{
  visible: boolean;
  title: string;
  /** true while the request is in flight */
  loading: boolean;
  result: HealthcheckResult | null;
}>();
const emit = defineEmits<{ (e: 'close'): void }>();

type RowStatus = 'pending' | 'running' | 'ok' | 'warn' | 'fail' | 'skipped';
interface Row extends Omit<HealthcheckStepView, 'state'> {
  status: RowStatus;
}

const PLACEHOLDER = buildHealthcheckSteps({
  ok: false, rtt_ms: 0, steps: [], target: '', agent_ip: '', agent_port: 0, ipv4_check_ok: false,
}).map(({ key, title }) => ({ key, title }));

// How many stages have been "revealed" with their final state. The server
// answers in one shot, so the stages are ticked off one by one for readability.
const revealed = ref(0);
let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const final = computed<HealthcheckStepView[]>(() =>
  props.result ? buildHealthcheckSteps(props.result) : [],
);

const running = computed(() => props.loading || (!!props.result && revealed.value < final.value.length));

const rows = computed<Row[]>(() =>
  PLACEHOLDER.map((p, i) => {
    const f = final.value[i];
    if (f && i < revealed.value) {
      const { state, ...rest } = f;
      return { ...rest, status: state === 'skipped' ? 'skipped' : state };
    }
    return { ...p, status: i === revealed.value && running.value ? 'running' : 'pending' };
  }),
);

const tick = () => {
  if (revealed.value >= final.value.length) return;
  revealed.value += 1;
  if (revealed.value < final.value.length) timer = setTimeout(tick, 280);
};

watch(
  () => [props.visible, props.loading, props.result] as const,
  ([visible, loading, result]) => {
    clearTimer();
    if (!visible || loading || !result) {
      revealed.value = 0;
      return;
    }
    revealed.value = 0;
    timer = setTimeout(tick, 200);
  },
  { immediate: true },
);
onBeforeUnmount(clearTimer);

const summary = computed(() => {
  const r = props.result;
  if (!r) return { level: 'fail', text: '' };
  if (!r.ok) return { level: 'fail', text: `检测失败：${r.error || '未知错误'}` };
  if (!r.ipv4_check_ok) return { level: 'warn', text: `代理可用，但 IP 检测失败 · 总耗时 ${r.rtt_ms} ms` };
  return { level: 'ok', text: `检测通过 · 总耗时 ${r.rtt_ms} ms` };
});
</script>

<style scoped>
.hc-steps { list-style: none; margin: 8px 0 0; padding: 0; }
.hc-step { position: relative; display: flex; gap: 14px; padding-bottom: 22px; }
.hc-step:last-child { padding-bottom: 4px; }
.hc-icon {
  flex: none; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
  font-size: 22px; z-index: 1; background: #fff;
}
.hc-dot { width: 10px; height: 10px; border-radius: 50%; border: 2px solid #d1d5db; }
.hc-line { position: absolute; left: 12px; top: 28px; bottom: 2px; width: 2px; background: #e5e7eb; }
.hc-body { flex: 1; min-width: 0; padding-top: 2px; }
.hc-title { font-size: 15px; color: #1f2937; display: flex; align-items: center; gap: 10px; }
.hc-ms { font-size: 12px; color: #6b7280; background: #f3f4f6; border-radius: 10px; padding: 0 8px; line-height: 20px; }
.hc-detail { margin-top: 4px; font-size: 13px; color: #6b7280; word-break: break-all; }

.is-pending .hc-title, .is-skipped .hc-title { color: #9ca3af; }
.is-running .hc-icon { color: #1677ff; }
.is-ok .hc-icon { color: #52c41a; }
.is-ok .hc-line { background: #b7eb8f; }
.is-warn .hc-icon { color: #faad14; }
.is-fail .hc-icon { color: #ff4d4f; }
.is-fail .hc-detail { color: #cf1322; }
.is-warn .hc-detail { color: #d48806; }
.is-ok .hc-icon { animation: hc-pop 0.3s ease-out; }

.hc-summary { margin-top: 18px; padding: 10px 14px; border-radius: 8px; font-size: 14px; }
.hc-summary.is-ok { background: #f6ffed; color: #389e0d; }
.hc-summary.is-warn { background: #fffbe6; color: #d48806; }
.hc-summary.is-fail { background: #fff2f0; color: #cf1322; }

@keyframes hc-pop { 0% { transform: scale(0.4); } 70% { transform: scale(1.2); } 100% { transform: scale(1); } }
</style>
